import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { LogOut, Download, X, MapPin, Phone, CalendarCheck2, Clock, CheckCircle2, XCircle, Copy, Store as StoreIcon, ChevronDown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://www.magniflex.com/frontend_assets/b2c/media/img/logo/magniflex-official-logo-it_IT.png";

const FRONTEND_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const LAST_STORE_KEY = "magni_owner_active_store";

export default function OwnerDashboard() {
    const { user, loading: authLoading, logout } = useAuth();
    const navigate = useNavigate();
    const [myStores, setMyStores] = useState([]);
    const [activeStoreId, setActiveStoreId] = useState(() => localStorage.getItem(LAST_STORE_KEY) || "");
    const [store, setStore] = useState(null);
    const [stats, setStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [performance, setPerformance] = useState(null);
    const [filters, setFilters] = useState({ status: "", date_from: "", date_to: "" });

    // Load list of stores this owner has access to
    useEffect(() => {
        if (!authLoading && user && (user.role === "store_owner" || user.role === "admin")) {
            api.get("/owner/my-stores").then((r) => {
                setMyStores(r.data || []);
                if (r.data && r.data.length) {
                    const stored = localStorage.getItem(LAST_STORE_KEY);
                    const valid = stored && r.data.some((s) => s.id === stored);
                    if (!valid) {
                        setActiveStoreId(r.data[0].id);
                        localStorage.setItem(LAST_STORE_KEY, r.data[0].id);
                    } else if (!activeStoreId) {
                        setActiveStoreId(stored);
                    }
                }
            }).catch(() => setMyStores([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, user]);

    const refresh = async () => {
        if (!activeStoreId) return;
        const params = { store_id: activeStoreId };
        Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
        const [s, b, st, perf] = await Promise.all([
            api.get("/owner/store", { params: { store_id: activeStoreId } }).catch(() => null),
            api.get("/owner/bookings", { params }),
            api.get("/owner/stats", { params: { store_id: activeStoreId } }),
            api.get("/owner/performance", { params: { store_id: activeStoreId } }).catch(() => null),
        ]);
        if (s) setStore(s.data);
        setBookings(
            [...b.data].sort(
                (a, b) =>
                    (b.slot_date + b.slot_time).localeCompare(a.slot_date + a.slot_time),
            ),
        );
        setStats(st.data);
        if (perf) setPerformance(perf.data);
    };

    useEffect(() => {
        if (!authLoading && user && (user.role === "store_owner" || user.role === "admin") && activeStoreId) {
            refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, user, filters, activeStoreId]);

    const handleStoreChange = (sid) => {
        setActiveStoreId(sid);
        localStorage.setItem(LAST_STORE_KEY, sid);
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-magni-cream text-magni-muted">
                Caricamento...
            </div>
        );
    }
    if (!user) return <Navigate to="/rivenditore/login" replace />;

    if (user.role !== "store_owner" && user.role !== "admin") {
        return <Navigate to="/rivenditore/login" replace />;
    }
    if (user.role === "admin" && !user.store_id) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-magni-cream px-4">
                <div className="max-w-md w-full bg-white border border-magni-light p-10 text-center">
                    <div className="overline mb-2" style={{ color: "#dc3545" }}>Accesso non consentito</div>
                    <h1 className="font-serif-display text-2xl text-magni-navy mb-3">
                        Quest'area è riservata ai rivenditori
                    </h1>
                    <p className="text-sm text-magni-muted mb-6">
                        Sei loggato come <strong>{user.email}</strong> (amministratore).
                        Vai al backoffice per gestire tutti i negozi e le prenotazioni.
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => navigate("/admin")}
                            className="magni-btn-primary"
                            style={{ padding: "10px 18px", fontSize: "12px" }}
                        >
                            Vai al backoffice
                        </button>
                        <button
                            onClick={async () => { await logout(); navigate("/rivenditore/login"); }}
                            className="magni-btn-secondary"
                            style={{ padding: "10px 18px", fontSize: "12px" }}
                        >
                            Esci
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const handleLogout = async () => {
        await logout();
        navigate("/rivenditore/login");
    };

    const checkIn = async (b) => {
        if (!window.confirm(`Confermare l'arrivo di ${b.first_name} ${b.last_name}? Verrà inviata l'email di richiesta feedback.`)) return;
        try {
            await api.post(`/owner/bookings/${b.id}/check-in`);
            toast.success("Cliente accolto. Email feedback in invio.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const recordPurchase = async (b, purchased) => {
        try {
            await api.post(`/owner/bookings/${b.id}/purchase`, { purchased });
            toast.success(purchased ? "Acquisto registrato 🎉" : "Esito registrato");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const cancel = async (b) => {
        if (!window.confirm(`Annullare la prenotazione di ${b.first_name} ${b.last_name}?`)) return;
        try {
            await api.post(`/owner/bookings/${b.id}/cancel`);
            toast.success("Prenotazione annullata. Email inviata al cliente.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const exportCsv = () => {
        const params = new URLSearchParams();
        if (activeStoreId) params.set("store_id", activeStoreId);
        Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
        const token = localStorage.getItem("magni_admin_token");
        fetch(`${API}/owner/bookings/export.csv?${params.toString()}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
            .then((r) => r.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "prenotazioni-negozio.csv";
                a.click();
                URL.revokeObjectURL(url);
            });
    };

    const copyAdvLink = () => {
        if (!store) return;
        const url = `${FRONTEND_ORIGIN}/negozio/${store.slug}`;
        navigator.clipboard.writeText(url);
        toast.success("Link ADV copiato negli appunti");
    };

    const tiles = [
        { label: "Oggi", value: stats?.today ?? "—", icon: Clock },
        { label: "Prossime", value: stats?.upcoming ?? "—", icon: CalendarCheck2 },
        { label: "Confermate", value: stats?.confirmed ?? "—", icon: CheckCircle2 },
        { label: "Annullate", value: stats?.cancelled ?? "—", icon: XCircle },
    ];

    return (
        <div className="min-h-screen bg-magni-cream">
            <Toaster position="top-center" richColors />

            {/* Top bar */}
            <header className="bg-magni-navy text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="Magniflex"
                            className="h-10 w-auto"
                            style={{ filter: "brightness(0) invert(1)" }}
                        />
                        <div className="hidden md:flex flex-col leading-none">
                            <span className="text-[10px] tracking-[0.22em] uppercase text-white/70 font-semibold">
                                Area rivenditore
                            </span>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4 text-sm">
                        <span data-testid="owner-email" className="text-white/80 hidden sm:inline">
                            {user.email}
                        </span>
                        <button
                            data-testid="owner-logout-btn"
                            onClick={handleLogout}
                            className="inline-flex items-center gap-1 text-white/80 hover:text-white"
                        >
                            <LogOut className="w-4 h-4" /> Esci
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Store switcher — shown only if owner has access to 2+ stores */}
                {myStores.length > 1 && (
                    <div
                        data-testid="store-switcher"
                        className="bg-magni-navy text-white border border-magni-navy p-4 sm:p-5 mb-6 flex flex-wrap items-center gap-3"
                    >
                        <StoreIcon className="w-5 h-5 flex-shrink-0" />
                        <div className="text-xs uppercase tracking-[0.15em] font-semibold text-white/80">
                            Stai gestendo
                        </div>
                        <div className="relative flex-1 min-w-[240px]">
                            <select
                                data-testid="store-switcher-select"
                                value={activeStoreId}
                                onChange={(e) => handleStoreChange(e.target.value)}
                                className="w-full appearance-none bg-white text-magni-navy border-0 px-4 py-3 pr-10 text-sm font-medium focus:outline-none cursor-pointer"
                            >
                                {myStores.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name} — {s.city}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-magni-navy pointer-events-none" />
                        </div>
                        <div className="text-xs text-white/70 hidden sm:block">
                            {myStores.length} negozi gestiti
                        </div>
                    </div>
                )}

                {/* Store header */}
                <div className="bg-white border border-magni-light p-6 sm:p-8 mb-8">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <div className="overline mb-2">Il tuo negozio</div>
                            <h1
                                data-testid="owner-store-name"
                                className="font-serif-display text-3xl sm:text-4xl text-magni-navy mb-3"
                            >
                                {store?.name || "Caricamento..."}
                            </h1>
                            {store && (
                                <div className="flex flex-wrap gap-4 text-sm text-magni-muted">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-magni-navy" />
                                        {store.address}, {store.city}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-magni-navy" />
                                        {store.phone}
                                    </div>
                                    {store.contact_person && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy">Referente</span>
                                            <span>{store.contact_person}</span>
                                        </div>
                                    )}
                                    {store.support_phone && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy">Assistenza</span>
                                            <a href={`tel:${store.support_phone}`} className="text-magni-navy hover:opacity-75">
                                                {store.support_phone}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {store && (
                            <button
                                onClick={copyAdvLink}
                                data-testid="copy-adv-link-btn"
                                className="magni-btn-secondary inline-flex items-center"
                                style={{ padding: "10px 18px", fontSize: "12px" }}
                            >
                                <Copy className="w-4 h-4 mr-2" /> Copia link ADV
                            </button>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {tiles.map((t) => (
                        <div key={t.label} className="bg-white border border-magni-light p-6">
                            <div className="flex items-center justify-between mb-3">
                                <span className="overline">{t.label}</span>
                                <t.icon className="w-5 h-5 text-magni-navy" />
                            </div>
                            <div className="font-serif-display text-4xl text-magni-navy">{t.value}</div>
                        </div>
                    ))}
                </div>

                {/* Performance vs network */}
                {performance && performance.store.bookings > 0 && (
                    <div className="bg-white border border-magni-light p-6 mb-8" data-testid="owner-performance">
                        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                            <div>
                                <div className="overline mb-1">Le tue performance</div>
                                <h2 className="font-serif-display text-2xl text-magni-navy">
                                    Come stai andando
                                </h2>
                            </div>
                            <div className="text-xs text-magni-muted">vs. media network</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                {
                                    label: "Tasso di presentazione",
                                    me: performance.store.show_rate,
                                    them: performance.network.show_rate,
                                    suffix: "%",
                                    tooltip: "Quanti clienti si sono effettivamente presentati (check-in / prenotazioni totali)",
                                    testid: "perf-show-rate",
                                },
                                {
                                    label: "Conversion all'acquisto",
                                    me: performance.store.conversion_rate,
                                    them: performance.network.conversion_rate,
                                    suffix: "%",
                                    tooltip: "Quanti hanno comprato dopo la prova (acquisti / check-in)",
                                    testid: "perf-conversion",
                                },
                                {
                                    label: "NPS medio (1-10)",
                                    me: performance.store.avg_nps,
                                    them: performance.network.avg_nps,
                                    suffix: "",
                                    tooltip: "Quanto i tuoi clienti ti raccomanderebbero",
                                    testid: "perf-nps",
                                },
                            ].map((m) => {
                                const meVal = m.me ?? 0;
                                const themVal = m.them ?? 0;
                                const delta = meVal - themVal;
                                const better = delta > 0.05;
                                const worse = delta < -0.05;
                                return (
                                    <div key={m.label} className="border border-magni-light p-5" data-testid={m.testid}>
                                        <div className="text-xs text-magni-muted mb-2 uppercase tracking-[0.12em]">
                                            {m.label}
                                        </div>
                                        <div className="font-serif-display text-4xl text-magni-navy">
                                            {m.me != null ? m.me : "—"}
                                            {m.me != null && m.suffix}
                                        </div>
                                        <div className="text-xs text-magni-muted mt-2">
                                            Network: <strong className="text-magni-navy">{m.them != null ? m.them : "—"}{m.them != null && m.suffix}</strong>
                                            {m.me != null && m.them != null && (
                                                <span
                                                    className="ml-2 inline-block px-2 py-0.5 rounded-none"
                                                    style={{
                                                        background: better ? "#e6f4ea" : worse ? "#fde6e6" : "#f1f1f1",
                                                        color: better ? "#0d6b3f" : worse ? "#a02020" : "#5b6c78",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {delta > 0 ? "+" : ""}
                                                    {Math.round(delta * 10) / 10}
                                                    {m.suffix}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-magni-muted mt-3 leading-relaxed">{m.tooltip}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-xs">
                            <div className="bg-magni-cream border border-magni-light p-3">
                                <div className="text-magni-muted uppercase tracking-[0.1em] mb-1">Check-in</div>
                                <div className="text-magni-navy font-semibold">{performance.store.checked_in}</div>
                            </div>
                            <div className="bg-magni-cream border border-magni-light p-3">
                                <div className="text-magni-muted uppercase tracking-[0.1em] mb-1">Acquisti</div>
                                <div className="text-magni-navy font-semibold">{performance.store.purchased}</div>
                            </div>
                            <div className="bg-magni-cream border border-magni-light p-3">
                                <div className="text-magni-muted uppercase tracking-[0.1em] mb-1">No-show</div>
                                <div className="text-magni-navy font-semibold">{performance.store.no_show}</div>
                            </div>
                            <div className="bg-magni-cream border border-magni-light p-3">
                                <div className="text-magni-muted uppercase tracking-[0.1em] mb-1">Feedback</div>
                                <div className="text-magni-navy font-semibold">{performance.store.feedback_count}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white border border-magni-light p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                            Stato
                        </label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            className="magni-input"
                        >
                            <option value="">Tutte</option>
                            <option value="confirmed">Confermate</option>
                            <option value="checked_in">Check-in fatto</option>
                            <option value="completed">Completate</option>
                            <option value="cancelled">Annullate</option>
                            <option value="no_show">No-show</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                            Da
                        </label>
                        <input
                            type="date"
                            value={filters.date_from}
                            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                            className="magni-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                            A
                        </label>
                        <input
                            type="date"
                            value={filters.date_to}
                            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                            className="magni-input"
                        />
                    </div>
                    <div className="flex items-end">
                        <button onClick={exportCsv} className="magni-btn-secondary w-full" style={{ padding: "12px" }}>
                            <Download className="w-4 h-4 mr-2" /> Esporta CSV
                        </button>
                    </div>
                </div>

                {/* Bookings list — mobile-first cards */}
                <div className="space-y-3" data-testid="bookings-list">
                    {bookings.length === 0 && (
                        <div className="bg-white border border-magni-light p-10 text-center text-magni-muted">
                            Nessuna prenotazione con i filtri selezionati.
                        </div>
                    )}
                    {bookings.map((b) => (
                        <BookingCard
                            key={b.id}
                            booking={b}
                            onCheckIn={() => checkIn(b)}
                            onCancel={() => cancel(b)}
                            onPurchase={(yes) => recordPurchase(b, yes)}
                        />
                    ))}
                </div>

                <div className="text-xs text-magni-muted mt-3" data-testid="owner-bookings-count">
                    {bookings.length} prenotazioni
                </div>
            </main>
        </div>
    );
}

function BookingCard({ booking: b, onCheckIn, onCancel, onPurchase }) {
    const eff = b.effective_status || b.status;
    const isPast = eff === "no_show";
    const isConfirmed = eff === "confirmed";
    const isCheckedIn = b.status === "checked_in";
    const isCompleted = b.status === "completed";
    const isCancelled = b.status === "cancelled";
    const askPurchase = b.status === "checked_in" && b.purchased == null;

    const statusBadge = () => {
        if (isCancelled)
            return { dot: "bg-red-500", label: "ANNULLATA", cls: "bg-red-50 text-red-700" };
        if (isPast)
            return { dot: "bg-red-500", label: "NON PRESENTATO", cls: "bg-red-50 text-red-700" };
        if (isCompleted)
            return {
                dot: "bg-green-600",
                label: b.purchased ? "COMPLETATA · ACQUISTO" : "COMPLETATA · NO ACQUISTO",
                cls: "bg-green-50 text-green-700",
            };
        if (isCheckedIn)
            return {
                dot: "bg-green-500 animate-pulse",
                label: "CLIENTE IN STORE",
                cls: "bg-green-50 text-green-700",
            };
        return { dot: "bg-magni-navy", label: "CONFERMATA", cls: "bg-magni-celeste text-magni-navy" };
    };
    const s = statusBadge();

    return (
        <div
            className="bg-white border border-magni-light overflow-hidden"
            data-testid={`owner-booking-${b.id}`}
        >
            {/* Header row */}
            <div className="p-4 sm:p-5 flex flex-wrap items-start justify-between gap-3 border-b border-magni-light">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="text-center bg-magni-cream border border-magni-light px-3 py-2 min-w-[68px]">
                        <div className="text-[10px] uppercase tracking-wider text-magni-muted font-semibold">
                            {new Date(b.slot_date + "T00:00:00").toLocaleDateString("it-IT", { month: "short" })}
                        </div>
                        <div className="text-2xl font-bold text-magni-navy leading-none">
                            {b.slot_date.slice(8, 10)}
                        </div>
                        <div className="text-xs text-magni-navy mt-1 font-semibold">
                            {b.slot_time}
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-magni-navy text-base sm:text-lg">
                            {b.first_name} {b.last_name}
                        </div>
                        <div className="text-xs text-magni-muted mt-1 space-y-0.5">
                            <div>
                                <a href={`tel:${b.phone}`} className="text-magni-navy hover:opacity-75 font-medium">
                                    {b.phone}
                                </a>
                            </div>
                            <div className="truncate">
                                <a href={`mailto:${b.email}`} className="hover:text-magni-navy">
                                    {b.email}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                        className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2.5 py-1.5 inline-flex items-center gap-1.5 ${s.cls}`}
                    >
                        <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
                        {s.label}
                    </span>
                </div>
            </div>

            {/* Notes */}
            {b.notes && (
                <div className="px-4 sm:px-5 py-3 bg-magni-cream border-b border-magni-light text-sm text-magni-navy">
                    <span className="text-[10px] uppercase tracking-wider text-magni-muted font-semibold mr-2">
                        Note cliente:
                    </span>
                    {b.notes}
                </div>
            )}

            {/* PURCHASE BANNER — shown only when checked_in and purchase not recorded */}
            {askPurchase && (
                <div
                    data-testid={`purchase-banner-${b.id}`}
                    className="bg-magni-navy text-white p-5 sm:p-6"
                >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-semibold mb-2">
                        Esito visita
                    </div>
                    <h3 className="font-serif-display text-xl sm:text-2xl leading-tight mb-4">
                        Il cliente {b.first_name} {b.last_name} ha acquistato MagniStretch®?
                    </h3>
                    <div className="flex gap-3">
                        <button
                            onClick={() => onPurchase(true)}
                            data-testid={`purchase-yes-${b.id}`}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 font-bold uppercase tracking-wider text-sm"
                        >
                            ✓ Sì, acquistato
                        </button>
                        <button
                            onClick={() => onPurchase(false)}
                            data-testid={`purchase-no-${b.id}`}
                            className="flex-1 bg-white/15 hover:bg-white/25 text-white py-3 font-bold uppercase tracking-wider text-sm border border-white/30"
                        >
                            ✗ No
                        </button>
                    </div>
                </div>
            )}

            {/* Actions */}
            <div className="p-4 sm:p-5 flex flex-wrap items-center gap-2 justify-end">
                {isConfirmed && (
                    <>
                        <button
                            onClick={onCancel}
                            data-testid={`cancel-${b.id}`}
                            className="text-xs uppercase tracking-wider font-semibold text-red-600 hover:text-red-800 px-3 py-2"
                        >
                            Annulla
                        </button>
                        <button
                            onClick={onCheckIn}
                            data-testid={`checkin-${b.id}`}
                            className="magni-btn-primary"
                            style={{ padding: "12px 22px", fontSize: "12px" }}
                        >
                            ✓ Cliente arrivato
                        </button>
                    </>
                )}
                {isCheckedIn && !askPurchase && (
                    <div className="text-xs text-magni-muted">Test in corso · attendi fine sessione per registrare l'esito</div>
                )}
                {isCompleted && (
                    <div className="text-xs text-magni-muted">
                        Esito acquisto:{" "}
                        <strong className={b.purchased ? "text-green-700" : "text-magni-navy"}>
                            {b.purchased ? "Sì" : "No"}
                        </strong>
                    </div>
                )}
            </div>
        </div>
    );
}
